"use client"

import { useCallback, useEffect, useMemo, useState } from "react"
import { usePathname, useSearchParams } from "next/navigation"
import { isIOS, isAndroid } from "react-device-detect"

const DISMISS_KEY = "hank_open_in_app_dismissed"
const CROSS_DOMAIN_BASE = "https://app.gethank.com"

const EMAIL_UTM_SOURCES = [
  "klaviyo",
  "sendgrid",
  "email",
  "newsletter",
  "mailchimp",
]

function isFromEmail(searchParams: URLSearchParams): boolean {
  const source = searchParams.get("utm_source")?.toLowerCase()
  const medium = searchParams.get("utm_medium")?.toLowerCase()
  if (medium === "email") return true
  if (source && EMAIL_UTM_SOURCES.includes(source)) return true
  return false
}

export function OpenInAppBanner() {
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    if (!isIOS && !isAndroid) return
    if (typeof window === "undefined") return

    const dismissed = sessionStorage.getItem(DISMISS_KEY)
    if (dismissed) return

    if (isFromEmail(searchParams)) {
      setVisible(true)
    }
  }, [searchParams])

  const openAppUrl = useMemo(() => {
    const params = new URLSearchParams()
    searchParams.forEach((value, key) => {
      params.set(key, value)
    })
    const query = params.toString()
    return `${CROSS_DOMAIN_BASE}${pathname}${query ? `?${query}` : ""}`
  }, [pathname, searchParams])

  const handleOpen = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault()
      const query = new URLSearchParams()
      searchParams.forEach((value, key) => query.set(key, value))
      const qs = query.toString()
      const customSchemeUrl = `get-hank://${pathname}${qs ? `?${qs}` : ""}`

      // Try custom URL scheme first (works on BrowserStack and real devices)
      window.location.href = customSchemeUrl

      // If the app didn't open (scheme unrecognized), fall back to Universal Link
      setTimeout(() => {
        if (!document.hidden) {
          window.location.href = openAppUrl
        }
      }, 1500)
    },
    [pathname, searchParams, openAppUrl],
  )

  const handleDismiss = useCallback(() => {
    setVisible(false)
    sessionStorage.setItem(DISMISS_KEY, "1")
  }, [])

  if (!visible) return null

  return (
    <div
      style={{
        position: "fixed",
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 9999,
        backgroundColor: "#8B4FE3",
        padding: "12px 16px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "12px",
        boxShadow: "0 -2px 10px rgba(0, 0, 0, 0.15)",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: "10px", flex: 1 }}>
        <img
          src="https://storage.googleapis.com/hank-app-assets/hank-icon-rounded.png"
          alt="Hank"
          width={40}
          height={40}
          style={{ borderRadius: "8px" }}
          onError={(e) => {
            ;(e.target as HTMLImageElement).style.display = "none"
          }}
        />
        <div>
          <div
            style={{
              color: "#FFFFFF",
              fontSize: "14px",
              fontWeight: 600,
              lineHeight: "18px",
            }}
          >
            Open in the Hank app
          </div>
          <div
            style={{
              color: "rgba(255, 255, 255, 0.8)",
              fontSize: "12px",
              lineHeight: "16px",
            }}
          >
            Get the full experience
          </div>
        </div>
      </div>
      <a
        href={openAppUrl}
        onClick={handleOpen}
        style={{
          backgroundColor: "#FFFFFF",
          color: "#8B4FE3",
          border: "none",
          borderRadius: "20px",
          padding: "8px 16px",
          fontSize: "14px",
          fontWeight: 600,
          cursor: "pointer",
          whiteSpace: "nowrap",
          textDecoration: "none",
        }}
      >
        Open
      </a>
      <button
        onClick={handleDismiss}
        aria-label="Dismiss"
        style={{
          background: "none",
          border: "none",
          color: "rgba(255, 255, 255, 0.8)",
          fontSize: "20px",
          cursor: "pointer",
          padding: "4px",
          lineHeight: 1,
        }}
      >
        &times;
      </button>
    </div>
  )
}
